/**
 * Vantage · Signals — the domain model (docs/signals-requirements.md).
 *
 * A signal is EVIDENCE emitted by a detection, not a ticket. The ticket-like
 * state (status, disposition, notes) is a separate TRIAGE record
 * (lib/signal-store.ts) because the backend stores none of it — only a
 * `falsepositive` flag and a remark. Storylines are the connected-signals
 * job's subgraphs (lib/signal-subgraphs.ts), never stored.
 *
 * Everything here is plain data + pure functions. Wire rows come in raw
 * (api-client returns Record<string, unknown> — shapes unverified) and
 * `readSignal` is the one tolerant door: it accepts the lowercase stream
 * vocabulary (`detectionname`, `suspectuser`) and the PascalCase workbook
 * vocabulary (`DetectionName`, `SuspectUser`) alike.
 */

import type { DetectionSeverity } from './detections'
import type { SignalRow } from '@bloo-team/api-client'

export type SignalSeverity = DetectionSeverity
export type EntityRole = 'Suspect' | 'Target'

export interface SignalEntity {
  role: EntityRole
  /** Field name with the role prefix stripped, first letter capitalised: 'User', 'Host', 'Hash'. */
  field: string
  value: string
}

export interface Signal {
  uuid: string
  /** ms epoch. */
  at: number
  scope: string
  detectionName: string
  /**
   * Workbook / model id when the row carries one; null on the stream shape.
   * Live rows: `$SourceId` (32-hex) is the id; `$Workbook` is the NAME
   * (captured 2026-10-05, docs/inventory/signals-queue-capture.md), so the
   * id wins and the name is only a fallback for rows without one.
   */
  detectionId: string | null
  /** The detection's lifecycle stage (`$Stage`: prod / beta / dev / test); null when unset. */
  stage: string | null
  /** 0..10 DetectionScore; null when only a severity label is present. */
  score: number | null
  severity: SignalSeverity
  confidence: string | null
  tactic: string
  technique: string
  subtechnique: string
  tags: string[]
  streams: string[]
  occurrences: number
  entities: SignalEntity[]
  rawEventQuery: string
  kb: string
  remark: string
  /** The legacy per-signal false-positive flag. */
  fp: boolean
  /** Every field the row carried — the JSON tab and the raw-event excerpt. */
  raw: SignalRow
}

/* ------------------------------------------------------------- disposition */

export type SignalStatus = 'open' | 'in_progress' | 'closed'

export type Disposition =
  | 'true_positive'
  | 'benign_true_positive'
  | 'false_positive_logic'
  | 'false_positive_data'
  | 'duplicate'
  | 'undetermined'

export const DISPOSITIONS: readonly Disposition[] = [
  'true_positive',
  'benign_true_positive',
  'false_positive_logic',
  'false_positive_data',
  'duplicate',
  'undetermined',
]

export const DISPOSITION_LABEL: Record<Disposition, string> = {
  true_positive: 'True positive',
  benign_true_positive: 'Benign true positive',
  false_positive_logic: 'False positive — rule logic',
  false_positive_data: 'False positive — bad data',
  duplicate: 'Duplicate',
  undetermined: 'Undetermined',
}

export const DISPOSITION_HINT: Record<Disposition, string> = {
  true_positive: 'Malicious or policy-violating activity confirmed.',
  benign_true_positive: 'The detection fired correctly on expected activity (admin work, red team, security test).',
  false_positive_logic: 'The rule matched activity it should not — offers a suppression rule for these entities.',
  false_positive_data: 'The data was wrong or incomplete (parsing, clock, duplicate ingest) — flags the source stream.',
  duplicate: 'Already handled under another signal or case.',
  undetermined: 'Could not be confirmed either way; closed for capacity.',
}

/** Sub-type for true positives (Defender / Google style), free but suggested. */
export const TP_TYPES = ['malicious activity', 'compromised account', 'malware', 'policy violation', 'other'] as const
export const BENIGN_TYPES = ['admin activity', 'red team', 'security test', 'known application', 'other'] as const

/** Which dispositions are a false positive, for the FP mirror and metrics. */
export function isFalsePositive(d: Disposition | null): boolean {
  return d === 'false_positive_logic' || d === 'false_positive_data'
}

/* ------------------------------------------------------------------ triage */

export interface TriageLogEntry {
  at: number
  who: string
  what: string
}

export interface SignalNote {
  id: string
  at: number
  who: string
  text: string
}

/**
 * Local triage record — one per signal uuid, created lazily on first touch.
 * THIS IS THE DRAFT API CONTRACT for needs-API ledger item 1 (signal
 * status/disposition/audit).
 */
export interface SignalTriage {
  schema: 1
  uuid: string
  status: SignalStatus
  disposition: Disposition | null
  dispositionType: string | null
  note: string
  updatedAt: number
  closedAt: number | null
  caseId: string | null
  huntId: string | null
  /** Raw-event rows the analyst pinned (excerpt only — quota rule). */
  pins: PinnedRow[]
  notes: SignalNote[]
  log: TriageLogEntry[]
}

export interface PinnedRow {
  id: string
  at: number
  /** field -> shortened value. */
  excerpt: Record<string, string>
  sourceQuery: string
}

export function emptyTriage(uuid: string): SignalTriage {
  return {
    schema: 1,
    uuid,
    status: 'open',
    disposition: null,
    dispositionType: null,
    note: '',
    updatedAt: 0,
    closedAt: null,
    caseId: null,
    huntId: null,
    pins: [],
    notes: [],
    log: [],
  }
}

/** Tolerant reader for stored triage records; null quarantines a corrupt one. */
export function readTriage(v: unknown): SignalTriage | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  if (typeof o.uuid !== 'string' || !o.uuid) return null
  const base = emptyTriage(o.uuid)
  const status = o.status
  const disposition = o.disposition
  return {
    ...base,
    status: status === 'open' || status === 'in_progress' || status === 'closed' ? status : 'open',
    disposition: DISPOSITIONS.includes(disposition as Disposition) ? (disposition as Disposition) : null,
    dispositionType: typeof o.dispositionType === 'string' ? o.dispositionType : null,
    note: typeof o.note === 'string' ? o.note : '',
    updatedAt: typeof o.updatedAt === 'number' ? o.updatedAt : 0,
    closedAt: typeof o.closedAt === 'number' ? o.closedAt : null,
    caseId: typeof o.caseId === 'string' ? o.caseId : null,
    huntId: typeof o.huntId === 'string' ? o.huntId : null,
    pins: Array.isArray(o.pins) ? (o.pins.filter(isPin) as PinnedRow[]) : [],
    notes: Array.isArray(o.notes) ? (o.notes.filter(isNote) as SignalNote[]) : [],
    log: Array.isArray(o.log) ? (o.log.filter(isLog) as TriageLogEntry[]) : [],
  }
}

const isPin = (p: unknown): boolean =>
  typeof p === 'object' && p !== null && typeof (p as PinnedRow).id === 'string' && typeof (p as PinnedRow).excerpt === 'object'
const isNote = (n: unknown): boolean =>
  typeof n === 'object' && n !== null && typeof (n as SignalNote).id === 'string' && typeof (n as SignalNote).text === 'string'
const isLog = (l: unknown): boolean =>
  typeof l === 'object' && l !== null && typeof (l as TriageLogEntry).at === 'number' && typeof (l as TriageLogEntry).what === 'string'

/* ---------------------------------------------------------------- severity */

/** DetectionScore bands — the legacy workbook mapping (workbook-blocks.ts). */
export function severityForScore(score: number): SignalSeverity {
  if (score >= 10) return 'critical'
  if (score >= 7) return 'high'
  if (score >= 4) return 'medium'
  if (score >= 1) return 'low'
  return 'info'
}

export function readSeverity(v: unknown): SignalSeverity | null {
  if (typeof v !== 'string') return null
  const s = v.trim().toLowerCase()
  if (s === 'critical' || s === 'high' || s === 'medium' || s === 'low') return s
  if (s === 'info' || s === 'informational' || s === 'none') return 'info'
  return null
}

/** Numeric weight used for storyline scoring when a row has no score. */
export const SEVERITY_SCORE: Record<SignalSeverity, number> = {
  critical: 10,
  high: 8,
  medium: 5,
  low: 2,
  info: 0,
}

export const SEVERITY_ORDER: readonly SignalSeverity[] = ['critical', 'high', 'medium', 'low', 'info']

/* ----------------------------------------------------------------- reader */

/**
 * Case-insensitive lookup across the candidate spellings a field has.
 * The live list/data endpoints prefix every field with `$` ($Uuid, $CNAMTime —
 * VERIFIED 2026-08-24, docs/inventory/signals-capture.json), the SIGNALS
 * stream serves the same fields lowercase and bare — so matching strips a
 * leading `$` and ignores case.
 */
export function pick(row: SignalRow, ...names: string[]): unknown {
  for (const n of names) {
    if (n in row && row[n] !== undefined) return row[n]
  }
  const bare = new Map<string, string>()
  for (const k of Object.keys(row)) bare.set(k.replace(/^\$/, '').toLowerCase(), k)
  for (const n of names) {
    const k = bare.get(n.replace(/^\$/, '').toLowerCase())
    if (k !== undefined && row[k] !== undefined) return row[k]
  }
  return undefined
}

function str(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return ''
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** `'["A","B"]'` (JSON-encoded, verbatim from live), `'A,B'`, or an array. */
function list(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(str).filter(Boolean)
  const s = str(v).trim()
  if (!s) return []
  if (s.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(s)
      if (Array.isArray(parsed)) return parsed.map(str).filter(Boolean)
    } catch {
      // fall through to the comma split
    }
  }
  return s.split(',').map((x) => x.trim()).filter(Boolean)
}

function epochMs(v: unknown): number | null {
  const n = num(v)
  if (n !== null) return n < 1e11 ? n * 1000 : n
  const s = str(v)
  if (!s) return null
  const t = Date.parse(s)
  return Number.isNaN(t) ? null : t
}

/** Keys that start with Suspect/Target but are not entities. */
const NON_ENTITY = new Set(['suspectlocation'])

/**
 * Entities are every populated `Suspect<Field>` / `Target<Field>` column —
 * the serialisation workbook-blocks.ts applySignalSpec writes. The field
 * keeps the legacy capitalisation so it round-trips into suppression terms
 * (`makeTermKey('Suspect', 'Host')` -> 'SuspectHost').
 */
export function readEntities(row: SignalRow): SignalEntity[] {
  const out: SignalEntity[] = []
  for (const [k, v] of Object.entries(row)) {
    const m = /^\$?(suspect|target)(.+)$/i.exec(k)
    if (!m) continue
    if (NON_ENTITY.has(k.replace(/^\$/, '').toLowerCase())) continue
    const value = str(v).trim()
    if (!value || value === 'null' || value === '-') continue
    const role: EntityRole = m[1]!.toLowerCase() === 'suspect' ? 'Suspect' : 'Target'
    const field = m[2]!.charAt(0).toUpperCase() + m[2]!.slice(1)
    out.push({ role, field, value })
  }
  return out.sort((a, b) => (a.role === b.role ? a.field.localeCompare(b.field) : a.role === 'Suspect' ? -1 : 1))
}

/** Tolerant reader — null when the row has no id or time. */
export function readSignal(row: SignalRow): Signal | null {
  const uuid = str(pick(row, 'uuid', 'signal_uuid', 'SignalUUID', 'id'))
  const at = epochMs(pick(row, 'cnamtime', 'CNAMTime', 'timestamp', 'time'))
  if (!uuid || at === null) return null
  const score = num(pick(row, 'DetectionScore', 'detectionscore', 'score'))
  const sevLabel = readSeverity(pick(row, 'DetectionSeverity', 'detectionseverity', 'severity'))
  const severity = sevLabel ?? (score !== null ? severityForScore(score) : 'info')
  const fpRaw = pick(row, 'falsepositive', 'FalsePositive', 'fp')
  return {
    uuid,
    at,
    scope: str(pick(row, 'scope', 'scope_id')),
    detectionName: str(pick(row, 'DetectionName', 'detectionname', 'name')),
    detectionId: str(pick(row, 'SourceId', 'wb_id', 'wbid', 'detection_id', 'Workbook', 'workbook')) || null,
    stage: str(pick(row, 'Stage', 'stage')).trim() || null,
    score,
    severity,
    confidence: str(pick(row, 'DetectionConfidence', 'detectionconfidence', 'confidence')) || null,
    tactic: str(pick(row, 'DetectionTactic', 'detectiontactic', 'tactic')),
    technique: str(pick(row, 'DetectionTechnique', 'detectiontechnique', 'technique')),
    subtechnique: str(pick(row, 'DetectionSubtechnique', 'detectionsubtechnique', 'subtechnique')),
    tags: list(pick(row, 'tag_list', 'tags', 'Tags')),
    streams: list(pick(row, 'sourcestream', 'SourceStream', 'streams')),
    occurrences: num(pick(row, 'occurrences', 'Occurrences')) ?? 1,
    entities: readEntities(row),
    rawEventQuery: str(pick(row, 'raw_event_query', 'RawEventQuery')),
    kb: str(pick(row, 'signal_kb', 'kb')),
    remark: str(pick(row, 'signal_remark', 'remark')),
    fp: fpRaw === true || str(fpRaw).toLowerCase() === 'true' || fpRaw === 1,
    raw: row,
  }
}

/** `$FirstSeen` (epoch ms) from the row, null when absent — the flat table's optional "first seen" column. */
export function signalFirstSeen(s: Pick<Signal, 'raw'>): number | null {
  return epochMs(pick(s.raw, 'FirstSeen', 'firstseen', 'first_seen'))
}

/** `$LastSeen` (epoch ms), null when absent. */
export function signalLastSeen(s: Pick<Signal, 'raw'>): number | null {
  return epochMs(pick(s.raw, 'LastSeen', 'lastseen', 'last_seen'))
}

/** `$SourceType` (live rows: `Workbook`), '' when absent. */
export function signalSourceType(s: Pick<Signal, 'raw'>): string {
  return str(pick(s.raw, 'SourceType', 'sourcetype', 'source_type')).trim()
}

/**
 * The legacy drawer's private flag (`is_private`), null when the row does not
 * carry it — then it is unknown, not false.
 */
export function signalPrivate(s: Pick<Signal, 'raw'>): boolean | null {
  const v = pick(s.raw, 'is_private', 'IsPrivate', 'isprivate')
  if (v === undefined || v === null || v === '') return null
  return v === true || v === 1 || str(v).toLowerCase() === 'true'
}

export function readSignals(rows: SignalRow[]): Signal[] {
  return rows.map(readSignal).filter((s): s is Signal => s !== null)
}

/* ------------------------------------------------------------- entity keys */

/** Entity identity ignores role: j.mendes as suspect in A and target in B is one entity. */
export function entityKey(e: Pick<SignalEntity, 'field' | 'value'>): string {
  return `${e.field.toLowerCase()}:${e.value.toLowerCase()}`
}

export function entityLabel(e: Pick<SignalEntity, 'field' | 'value'>): string {
  return `${e.field.toLowerCase()} = ${e.value}`
}

/** Effective score: DetectionScore when present, else the severity weight. */
export function signalWeight(s: Pick<Signal, 'score' | 'severity'>): number {
  return s.score ?? SEVERITY_SCORE[s.severity]
}

/* --------------------------------------------------------------- promotion */

export interface PromotionRule {
  /** Default 50 — total_weight scale (decision S34: the live sample runs 16–88, so 12 / 3 promoted everything). */
  scoreThreshold: number
  /** Default 4 distinct DetectionTactic values. */
  tacticThreshold: number
}

export const DEFAULT_PROMOTION: PromotionRule = { scoreThreshold: 50, tacticThreshold: 4 }

export function isPromoted(s: { score: number; tactics: readonly string[] }, rule: PromotionRule = DEFAULT_PROMOTION): boolean {
  return s.score >= rule.scoreThreshold || s.tactics.length >= rule.tacticThreshold
}

/* -------------------------------------------------------------- kill chain */

/**
 * Coarse kill-chain stages for the storyline stripe — every ATT&CK tactic
 * maps onto one of seven boxes so progression reads left to right.
 */
export const KILL_CHAIN = [
  'recon',
  'access',
  'execution',
  'persistence',
  'credential',
  'lateral',
  'impact',
] as const
export type KillChainStage = (typeof KILL_CHAIN)[number]

const TACTIC_STAGE: Record<string, KillChainStage> = {
  reconnaissance: 'recon',
  'resource development': 'recon',
  'initial access': 'access',
  execution: 'execution',
  persistence: 'persistence',
  'privilege escalation': 'persistence',
  'defense evasion': 'execution',
  'credential access': 'credential',
  discovery: 'lateral',
  'lateral movement': 'lateral',
  collection: 'impact',
  'command and control': 'lateral',
  exfiltration: 'impact',
  impact: 'impact',
}

export function stageForTactic(tactic: string): KillChainStage | null {
  return TACTIC_STAGE[tactic.trim().toLowerCase()] ?? null
}

export function stagesFor(tactics: string[]): Set<KillChainStage> {
  const out = new Set<KillChainStage>()
  for (const t of tactics) {
    const s = stageForTactic(t)
    if (s) out.add(s)
  }
  return out
}

/* ----------------------------------------------------------------- reason */

export interface ReasonContext {
  /** How many other signals this detection produced on the anchor entity in the window. */
  priorOnEntity?: number
  /** Storyline (subgraph) score, when the signal sits in a promoted one. */
  storylineScore?: number | null
}

/**
 * The one-sentence reason (Elastic "alert reason" pattern) built from the
 * spec — deterministic, no LLM (decision S10). Reads: "<detection> — <suspect>
 * against <target>, N times; first time on this host."
 */
export function reasonSentence(s: Signal, ctx: ReasonContext = {}): string {
  const suspects = s.entities.filter((e) => e.role === 'Suspect')
  const targets = s.entities.filter((e) => e.role === 'Target')
  const who = suspects.map((e) => `${e.field.toLowerCase()} ${e.value}`).join(', ')
  const what = targets.map((e) => `${e.field.toLowerCase()} ${e.value}`).join(', ')
  const parts: string[] = []
  if (who && what) parts.push(`${who} against ${what}`)
  else if (who) parts.push(`by ${who}`)
  else if (what) parts.push(`on ${what}`)
  if (s.occurrences > 1) parts.push(`${s.occurrences} occurrences`)
  const head = `${s.detectionName || 'Detection'}${parts.length ? ' — ' + parts.join(', ') : ''}.`
  const tail: string[] = []
  if (ctx.priorOnEntity === 0) tail.push('First time this detection fired on this entity in the window.')
  else if (typeof ctx.priorOnEntity === 'number' && ctx.priorOnEntity > 0) {
    tail.push(`${ctx.priorOnEntity} earlier signal${ctx.priorOnEntity === 1 ? '' : 's'} from the same detection on this entity.`)
  }
  if (typeof ctx.storylineScore === 'number' && ctx.storylineScore > 0) {
    tail.push(`Part of a storyline scoring ${ctx.storylineScore}.`)
  }
  return [head, ...tail].join(' ')
}

/* ----------------------------------------------------------------- filters */

export interface SignalFilter {
  severities: SignalSeverity[]
  tactics: string[]
  detections: string[]
  streams: string[]
  /** Substring over entity values. */
  entity: string
  tags: string[]
  /** True on the Signals page but in its False positives queue (no control); kept for the server filter mapping. */
  hideFp: boolean
  /** Only signals flagged false positive — the Signals page's False positives queue. Wins over hideFp. */
  fpOnly?: boolean
  /** Only open + in-progress when true. Fixed false on the Signals page (no control). */
  openOnly: boolean
}

export const EMPTY_FILTER: SignalFilter = {
  severities: [],
  tactics: [],
  detections: [],
  streams: [],
  entity: '',
  tags: [],
  hideFp: true,
  openOnly: false,
}

export function matchesFilter(s: Signal, f: SignalFilter, triage?: Pick<SignalTriage, 'status'> | null): boolean {
  if (f.fpOnly ? !s.fp : f.hideFp && s.fp) return false
  if (f.severities.length && !f.severities.includes(s.severity)) return false
  if (f.tactics.length && !f.tactics.includes(s.tactic)) return false
  if (f.detections.length && !f.detections.includes(s.detectionName)) return false
  if (f.streams.length && !s.streams.some((st) => f.streams.includes(st))) return false
  if (f.tags.length && !s.tags.some((t) => f.tags.includes(t))) return false
  if (f.openOnly && triage && triage.status === 'closed') return false
  if (f.entity.trim()) {
    const q = f.entity.trim().toLowerCase()
    if (!s.entities.some((e) => e.value.toLowerCase().includes(q))) return false
  }
  return true
}

/* ----------------------------------------------------------------- metrics */

export interface SignalMetrics {
  open: number
  bySeverity: Record<SignalSeverity, number>
  closed: number
  /** Mean minutes from signal time to first triage touch, over closed/in-progress. */
  mttaMinutes: number | null
  fpRate: number | null
}

export function signalMetrics(signals: Signal[], triage: Map<string, SignalTriage>): SignalMetrics {
  const bySeverity: Record<SignalSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 }
  let open = 0
  let closed = 0
  let fp = 0
  let ackSum = 0
  let ackN = 0
  for (const s of signals) {
    const t = triage.get(s.uuid)
    const status = t?.status ?? 'open'
    if (status === 'closed') {
      closed++
      if (t && isFalsePositive(t.disposition)) fp++
    } else {
      open++
      bySeverity[s.severity]++
    }
    const first = t?.log[0]?.at
    if (first && first > s.at) {
      ackSum += (first - s.at) / 60_000
      ackN++
    }
  }
  return {
    open,
    bySeverity,
    closed,
    mttaMinutes: ackN ? Math.round(ackSum / ackN) : null,
    fpRate: closed ? fp / closed : null,
  }
}

/* -------------------------------------------------------------------- ids */

export function signalLocalId(prefix = 'sg'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10)
  return `${prefix}_${Date.now().toString(36)}_${rand}`
}
